/**
 * Onboarding Persistence Hooks
 *
 * Production-ready React Query hooks for managing onboarding session state.
 * Integrates with Supabase RPC functions for data persistence.
 *
 * Architecture:
 * - Backend as source of truth
 * - Optimistic locking via version field
 * - Automatic retry with exponential backoff
 * - Cache invalidation on mutations
 *
 * @see E:\Job\IT\50Studios\Fiels\Clickup_Projects\projects\family-tree\project\supabase\migrations\20260324000001_onboarding_persistence.sql
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/config/supabase';
import type {
  FetchOnboardingSessionOutput,
  SaveOnboardingStepOutput,
  AnyStepData,
} from '@/types/onboarding';

// =====================================================
// Query Keys
// =====================================================

export const onboardingKeys = {
  all: ['onboarding'] as const,
  session: (userId: string) => [...onboardingKeys.all, 'session', userId] as const,
};

// =====================================================
// Type Definitions
// =====================================================

interface SaveStepParams {
  step: number;
  stepData: AnyStepData;
  expectedVersion?: number;
}

interface SaveStepError {
  message: string;
  code?: string;
  isVersionConflict?: boolean;
}

// =====================================================
// Fetch Onboarding Session Hook
// =====================================================

/**
 * Fetch user's onboarding session from database
 *
 * Returns:
 * - session_id: UUID of the session
 * - current_step: Current step number (1-5)
 * - completed_steps: Array of completed step numbers
 * - draft_data: All saved form data
 * - version: Current version for optimistic locking
 * - last_saved_at: ISO timestamp of last save
 *
 * Cache behavior:
 * - Cached for 5 minutes (staleTime from queryClient defaults)
 * - Automatically refetched on window focus (can be disabled)
 * - Can be manually invalidated via queryClient
 *
 * @param userId - The authenticated user's ID
 * @param options - React Query options (enabled, refetchOnMount, etc.)
 */
export function useFetchOnboardingSession(
  userId: string,
  options?: {
    enabled?: boolean;
    refetchOnMount?: boolean;
  }
) {
  return useQuery({
    queryKey: onboardingKeys.session(userId),
    queryFn: async (): Promise<FetchOnboardingSessionOutput | null> => {
      // Type assertion needed due to Supabase client type limitations with RPC functions
      const { data, error } = await supabase.rpc('fetch_onboarding_session', {
        p_user_id: userId,
      } as any);

      if (error) {
        // If error is "No session found", return null instead of throwing
        // This is normal for first-time users
        if (error.message?.includes('No session found') || error.code === 'PGRST116') {
          return null;
        }

        console.error('Failed to fetch onboarding session:', error);
        throw new Error(error.message || 'Failed to fetch onboarding session');
      }

      // RPC functions with RETURNS TABLE return arrays, get first element
      const resultArray = data as unknown as FetchOnboardingSessionOutput[];
      return (resultArray && resultArray.length > 0 ? resultArray[0] : null) ?? null;
    },
    enabled: options?.enabled ?? true,
    refetchOnMount: options?.refetchOnMount ?? true,
    retry: (failureCount, error) => {
      // Don't retry on "no session found" errors
      if (error instanceof Error && error.message.includes('No session found')) {
        return false;
      }
      // Retry network errors up to 2 times
      return failureCount < 2;
    },
  });
}

// =====================================================
// Save Onboarding Step Hook
// =====================================================

/**
 * Save step data to database via RPC function
 *
 * Features:
 * - Optimistic locking: Detects concurrent edits via version field
 * - Retry logic: 3 attempts with exponential backoff
 * - Cache invalidation: Automatically refreshes session data on success
 * - Error handling: Returns user-friendly error messages
 *
 * Flow:
 * 1. Call save_onboarding_step RPC with step data and expected version
 * 2. RPC validates version and saves data
 * 3. On success: Invalidate session cache to trigger refetch
 * 4. On version conflict: Return specific error for UI handling
 *
 * @param userId - The authenticated user's ID
 */
export function useSaveOnboardingStep(userId: string) {
  const queryClient = useQueryClient();

  return useMutation<
    SaveOnboardingStepOutput,
    SaveStepError,
    SaveStepParams
  >({
    mutationFn: async ({ step, stepData, expectedVersion }) => {
      console.log('[useSaveOnboardingStep] Calling RPC with:', { userId, step, stepData, expectedVersion });

      // Type assertion needed due to Supabase client type limitations with RPC functions
      // Database function signature: save_onboarding_step(p_user_id, p_step, p_step_data)
      const { data, error } = await supabase.rpc('save_onboarding_step', {
        p_user_id: userId,
        p_step: step, // Fixed: was p_step_number, should be p_step
        p_step_data: stepData as any, // Cast needed for JSONB type
      } as any);

      console.log('[useSaveOnboardingStep] RPC response:', { data, error });

      if (error) {
        console.error('[useSaveOnboardingStep] Supabase error:', error);
        console.error('[useSaveOnboardingStep] Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
        });

        // Check for version conflict
        if (error.message?.includes('version conflict') || error.code === 'P0001') {
          throw {
            message: 'Session was updated elsewhere. Please refresh and try again.',
            code: error.code,
            isVersionConflict: true,
          } as SaveStepError;
        }

        throw {
          message: error.message || 'Failed to save onboarding step',
          code: error.code,
          isVersionConflict: false,
        } as SaveStepError;
      }

      // RPC functions with RETURNS TABLE return arrays, get first element
      const resultArray = data as unknown as SaveOnboardingStepOutput[];
      const result = resultArray[0];
      console.log('[useSaveOnboardingStep] Parsed result:', result);

      // Check if RPC function returned an error
      if (!result || !result.success) {
        console.error('[useSaveOnboardingStep] RPC returned failure:', result?.error_message);
        throw {
          message: result?.error_message || 'Failed to save onboarding step',
          isVersionConflict: result?.error_message?.includes('version conflict'),
        } as SaveStepError;
      }

      return result;
    },
    retry: (failureCount, error) => {
      // Don't retry version conflicts - user needs to refresh
      if (error.isVersionConflict) {
        return false;
      }
      // Retry network/server errors up to 3 times with exponential backoff
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => {
      // Exponential backoff: 1s, 2s, 4s
      return Math.min(1000 * 2 ** attemptIndex, 4000);
    },
    onSuccess: () => {
      // Invalidate session cache to trigger refetch with updated data
      queryClient.invalidateQueries({
        queryKey: onboardingKeys.session(userId),
      });
    },
  });
}

// =====================================================
// Complete Onboarding Hook (Stub for Phase 2)
// =====================================================

/**
 * Complete the onboarding wizard
 *
 * This hook will be fully implemented in Phase 3 when Step 5 UI is complete.
 * It calls the complete_onboarding_wizard RPC function which:
 * 1. Validates that steps 2-3 (parents) are complete
 * 2. Creates the family tree (auto-named from father's last name)
 * 3. Creates 2-6 person records (parents + optional grandparents)
 * 4. Creates 3-9 relationship records (spouse + parent_child)
 * 5. Marks onboarding as complete in user profile
 * 6. Cleans up the onboarding session
 *
 * Returns:
 * - tree_id: UUID of created tree
 * - first_person_id: UUID of father (legacy field)
 * - person_count: Number of persons created (2-6)
 * - relationship_count: Number of relationships created (3-9)
 * - success: Boolean indicating success
 * - error_message: Error message if failed
 *
 * TODO: Implement in Phase 3 when Step 5 UI is complete
 *
 * @param userId - The authenticated user's ID
 */
export function useCompleteOnboarding(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // TODO: Implement in Phase 2
      // const { data, error } = await supabase.rpc('complete_onboarding_wizard', {
      //   p_user_id: userId,
      // });
      //
      // if (error) {
      //   throw new Error(error.message || 'Failed to complete onboarding');
      // }
      //
      // return data as CompleteOnboardingWizardOutput;

      throw new Error('useCompleteOnboarding is not yet implemented. Will be completed in Phase 3.');
    },
    onSuccess: () => {
      // On success, invalidate session and trigger profile refresh
      queryClient.invalidateQueries({
        queryKey: onboardingKeys.session(userId),
      });

      // Also invalidate user profile to get updated onboarding_completed_at
      // This will trigger route guard to redirect to dashboard
      queryClient.invalidateQueries({
        queryKey: ['user', 'profile', userId],
      });
    },
  });
}

// =====================================================
// Utility Functions
// =====================================================

/**
 * Check if a specific step is accessible based on completed steps
 *
 * Rules:
 * - Step 1 is always accessible (welcome screen)
 * - Step N is accessible if Step N-1 is in completed_steps
 *
 * Example:
 * - completed_steps = [1, 2]
 * - Step 1: accessible (always)
 * - Step 2: accessible (step 1 completed)
 * - Step 3: accessible (step 2 completed)
 * - Step 4: not accessible (step 3 not completed)
 * - Step 5: not accessible (step 4 not completed)
 *
 * @param targetStep - The step number to check (1-5)
 * @param completedSteps - Array of completed step numbers
 */
export function isStepAccessible(
  targetStep: number,
  completedSteps: number[]
): boolean {
  if (targetStep === 1) {
    return true; // Welcome screen is always accessible
  }

  // Step N is accessible if Step N-1 is completed
  return completedSteps.includes(targetStep - 1);
}

/**
 * Get the next accessible step based on completed steps
 *
 * Logic:
 * - If no steps completed, return 1 (welcome)
 * - If all steps completed, return 5 (final step)
 * - Otherwise, return the first incomplete step
 *
 * @param completedSteps - Array of completed step numbers
 */
export function getNextStep(completedSteps: number[]): number {
  if (completedSteps.length === 0) {
    return 1;
  }

  // Find first incomplete step
  for (let step = 1; step <= 5; step++) {
    if (!completedSteps.includes(step)) {
      return step;
    }
  }

  // All steps completed
  return 5;
}
